/**
 * Single field label row with inline editing
 */

import { useState, useRef, useEffect } from 'react'
import { Check, X, Eye, EyeOff, Pencil } from 'lucide-react'
import { useUpdateFieldLabel } from '../hooks/queries'
import type { DataSourceField } from '../types'
import { cn } from '../../lib/utils'

interface FieldLabelRowProps {
  field: DataSourceField
  sourceTable: string
}

const TYPE_BADGES: Record<string, { label: string; className: string }> = {
  integer: { label: 'int', className: 'bg-blue-100 text-blue-700' },
  decimal: { label: 'dec', className: 'bg-green-100 text-green-700' },
  text: { label: 'txt', className: 'bg-gray-100 text-gray-700' },
}

export function FieldLabelRow({ field, sourceTable }: FieldLabelRowProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(field.custom_label || '')
  const inputRef = useRef<HTMLInputElement>(null)

  const updateMutation = useUpdateFieldLabel(sourceTable)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = async () => {
    const newLabel = editValue.trim() || null
    // Only save if value actually changed
    if (newLabel !== (field.custom_label || null)) {
      await updateMutation.mutateAsync({
        labelId: field.id,
        data: { custom_label: newLabel },
      })
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditValue(field.custom_label || '')
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const handleToggleVisibility = async () => {
    await updateMutation.mutateAsync({
      labelId: field.id,
      data: { is_visible: !field.is_visible },
    })
  }

  const typeBadge = TYPE_BADGES[field.data_type] || TYPE_BADGES.text
  const isCustomized = field.custom_label !== null

  return (
    <div
      className={cn(
        'grid grid-cols-12 gap-4 px-4 py-2 items-center',
        !field.is_visible && 'opacity-50'
      )}
    >
      {/* Field Name */}
      <div className="col-span-3">
        <code className="text-sm text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
          {field.field_name}
        </code>
      </div>

      {/* Default Label */}
      <div className="col-span-3 text-sm text-gray-500">
        {field.default_label}
      </div>

      {/* Display Label (editable) */}
      <div className="col-span-4">
        {isEditing ? (
          <div className="flex items-center gap-1">
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={field.default_label}
              className="flex-1 px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
              title="Speichern"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancel}
              className="p-1 text-gray-400 hover:bg-gray-50 rounded"
              title="Abbrechen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 group">
            <span
              className={cn(
                'text-sm',
                isCustomized ? 'text-blue-600 font-medium' : 'text-gray-900'
              )}
            >
              {field.display_label}
            </span>
            <button
              onClick={() => {
                setEditValue(field.custom_label || '')
                setIsEditing(true)
              }}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              title="Bearbeiten"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Type Badge */}
      <div className="col-span-1 text-center">
        <span className={cn('text-xs px-1.5 py-0.5 rounded', typeBadge.className)}>
          {typeBadge.label}
        </span>
      </div>

      {/* Visibility Toggle */}
      <div className="col-span-1 text-center">
        <button
          onClick={handleToggleVisibility}
          disabled={updateMutation.isPending}
          className={cn(
            'p-1 rounded transition-colors',
            field.is_visible
              ? 'text-green-600 hover:bg-green-50'
              : 'text-gray-400 hover:bg-gray-100'
          )}
          title={field.is_visible ? 'Ausblenden' : 'Einblenden'}
        >
          {field.is_visible ? (
            <Eye className="w-4 h-4" />
          ) : (
            <EyeOff className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  )
}
